import { Injectable, OnInit, EventEmitter } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/do';

import { RoleDefinition } from './role-definition';

function DEFAULT_ROLE_TEST() {
  return false;
}

@Injectable()
export class PermissionService implements CanActivate {
  public linkedURL = '';
  public onPermissionChange: EventEmitter<any> = new EventEmitter();
  private definitions: Array<RoleDefinition> = [];
  private rolesInheritance: { [roleName: string]: Array<string>};

  constructor(
    private router: Router
  ) { }

  public defineRole(
    role: string | Array<string>,
    testFunction: () => boolean | Observable<boolean> | Promise<boolean> = DEFAULT_ROLE_TEST,
    inheritedRoles: Array<string> = []
  ): void {
    let rolesNamesArray: Array<string> = this.forceArray(role);
    inheritedRoles = this.removeDuplicatedRoles(inheritedRoles);

    for (let roleName of rolesNamesArray) {
      this.addRoleDefinition(roleName, testFunction, inheritedRoles);
    }

    this.updateInheritances();
  }

  public permissionChanged(): void {
    this.onPermissionChange.emit();
  }

  public hasPermission(requestedRole: string | Array<string>): Observable<boolean> {
    let requestedRoleArray: Array<string> = this.forceArray(requestedRole);

    for (let roleName of requestedRoleArray) {
      requestedRoleArray = requestedRoleArray.concat(this.getKnownInherits(roleName));
    }

    requestedRoleArray = this.removeDuplicatedRoles(requestedRoleArray);

    let rolesResults: any[] = this.definitions
      .filter(
        (definition: RoleDefinition): boolean =>
          requestedRoleArray.indexOf(definition.getRoleName()) >= 0
      )
      .map(
        (definition: RoleDefinition): Observable<boolean> =>
          definition.mergeTests()
      );

    rolesResults.push((...results: Array<boolean>): boolean =>
      results.reduce((prev: boolean, current: boolean): boolean => prev || current)
    );

    return Observable.forkJoin.apply(this, rolesResults);
  }

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> | Promise<boolean> {
    if (undefined === route.data['only']) {
      return true;
    }

    let routeRoles: string | Array<string> = route.data['only'] as (string | Array<string>);

    return this.hasPermission(routeRoles)
    .do((canActivate: boolean) => {
      if (!canActivate && undefined !== route.data['redirectTo']) {
          this.linkedURL = state.url;
          this.router.navigate([route.data['redirectTo'] as string]);
        }
      });
  }

  public getRoleDefinition(roleName: string): RoleDefinition {
    for (let existingDefinition of this.definitions) {
      if (roleName === existingDefinition.getRoleName()) {
        return existingDefinition;
      }
    }

    return null;
  }

  private addRoleDefinition(
    roleName: string,
    testFunction: () => boolean | Observable<boolean> | Promise<boolean>,
    inheritedRoles: Array<string>
  ) {
    for (let existingDefinition of this.definitions) {
      if (roleName === existingDefinition.getRoleName()) {
        existingDefinition.addTest(testFunction);
        existingDefinition.addInheritedRoles(inheritedRoles);

        return;
      }
    }

    this.definitions.push(new RoleDefinition(roleName, testFunction, inheritedRoles));
  }

  public getKnownInherits(roleName: string): Array<string> {
    if (this.rolesInheritance.hasOwnProperty(roleName)) {
      return this.rolesInheritance[roleName];
    }

    return [];
  }

  private updateInheritances() {
    this.rolesInheritance = {};

    for (let definition of this.definitions) {
      this.propagateInheritance(definition.getRoleName(), definition.getInheritedRoles());
    }

    for (let roleName in this.rolesInheritance) {
      if (!this.rolesInheritance.hasOwnProperty(roleName)) {
        continue;
      }

      this.rolesInheritance[roleName] = this.removeDuplicatedRoles(this.rolesInheritance[roleName]);
    }
  }

  private propagateInheritance(inheritedRoleName: string, parentRolesNames: Array<string>) {
    let parentDefinitions: Array<RoleDefinition> = parentRolesNames
      .map((parentRoleName: string): RoleDefinition => this.getRoleDefinition(parentRoleName))
      .filter((roleDefinition: RoleDefinition): boolean => roleDefinition !== null);

    for (let definition of parentDefinitions) {
      let definitionRoleName: string = definition.getRoleName();

      if (!this.rolesInheritance.hasOwnProperty(definitionRoleName)) {
        this.rolesInheritance[definitionRoleName] = [];
      }

      this.rolesInheritance[definitionRoleName].push(inheritedRoleName);
      this.propagateInheritance(inheritedRoleName, definition.getInheritedRoles());
    }
  }

  private forceArray(role: string | Array<string>): Array<string> {
    if (role instanceof Array) {

      return role;
    }

    return [role];
  }

  private removeDuplicatedRoles(roles: Array<string>): Array<string> {
    let uniqueRoles: Array<string> = [];

    for (let role of roles) {
      if (uniqueRoles.indexOf(role) === -1) {
        uniqueRoles.push(role);
      }
    }

    return uniqueRoles;
  }
}



// WEBPACK FOOTER //
// /dist/src/app/security/permission/permission.service.ts